import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'sonner';

import './styles/global.css';

import { createAppRouter } from '@/app/router';
import { OperatorProvider } from '@/features/auth/OperatorProvider';
import { useMocks } from '@/lib/environment';
import { createQueryClient } from '@/lib/queryClient';
import { ThemeProvider } from '@/lib/theme/ThemeProvider';

const queryClient = createQueryClient();
const router = createAppRouter(queryClient);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Missing #root element');
}

/**
 * Start the mock layer before the first render when VITE_USE_MOCKS is on, so screens
 * are developable without mesh-admin-api and a live Mongo. `useMocks` is already
 * hard-disabled whenever the active environment is production.
 */
async function startMocks(): Promise<void> {
  if (!useMocks) return;
  const { worker } = await import('./mocks/browser');
  await worker.start({ onUnhandledRequest: 'bypass', quiet: true });
}

await startMocks();

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <OperatorProvider>
          <RouterProvider router={router} />
          <Toaster
            position="bottom-right"
            // Toasts report the outcome of writes, so they must not auto-dismiss
            // before an operator on a phone has read them.
            duration={6000}
            toastOptions={{
              style: {
                background: 'var(--elev)',
                border: '1px solid var(--bd-s)',
                color: 'var(--tx)',
              },
            }}
          />
        </OperatorProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
